import { useState, useEffect, useRef } from 'react';
import { ChevronDown, FileText, Eye, X } from 'lucide-react';
import { api } from '../services/api';

const VARIABLES = [
  { token: '{{razao_social}}',        label: 'Razão Social' },
  { token: '{{nome_fantasia}}',        label: 'Nome Fantasia' },
  { token: '{{contato_nome}}',         label: 'Nome do Contato' },
  { token: '{{produto}}',              label: 'Produto' },
  { token: '{{valor_saas}}',           label: 'Valor SaaS' },
  { token: '{{valor_proposta}}',       label: 'Valor Proposta' },
  { token: '{{previsao_fechamento}}',  label: 'Previsão de Fechamento' },
  { token: '{{stage_atual}}',          label: 'Stage Atual' },
  { token: '{{nome_esn}}',             label: 'Nome do ESN' },
];

/**
 * Substitui variáveis no cliente para preview (espelha interpolate() do server)
 */
function previewInterpolate(text, lead) {
  if (!text || !lead) return text || '';
  return text
    .replace(/\{\{razao_social\}\}/g, lead.razao_social || '')
    .replace(/\{\{nome_fantasia\}\}/g, lead.nome_fantasia || lead.razao_social || '')
    .replace(/\{\{contato_nome\}\}/g, lead.contato_nome || '')
    .replace(/\{\{produto\}\}/g, lead.produto_nome || '')
    .replace(/\{\{valor_saas\}\}/g, lead.valor_saas ? `R$ ${Math.round(lead.valor_saas).toLocaleString('pt-BR')}` : '')
    .replace(/\{\{valor_proposta\}\}/g, lead.valor_saas ? `R$ ${Math.round(lead.valor_saas).toLocaleString('pt-BR')}` : '')
    .replace(/\{\{previsao_fechamento\}\}/g, lead.previsao_fechamento || '')
    .replace(/\{\{stage_atual\}\}/g, lead.stage || '')
    .replace(/\{\{nome_esn\}\}/g, lead.esn_nome || '');
}

/**
 * Botão de inserção de variável no cursor de um textarea.
 */
function insertAtCursor(textarea, token) {
  if (!textarea) return '';
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const before = textarea.value.substring(0, start);
  const after = textarea.value.substring(end);
  const newVal = before + token + after;
  // Move cursor after inserted token
  setTimeout(() => {
    textarea.selectionStart = textarea.selectionEnd = start + token.length;
    textarea.focus();
  }, 0);
  return newVal;
}

/**
 * Toolbar de variáveis para um textarea (insert at cursor).
 */
export function VariableToolbar({ textareaRef, value, onChange, label = 'Inserir variável' }) {
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative inline-block" ref={dropRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 border border-purple-200 hover:border-purple-400 rounded px-2 py-1 transition-colors"
      >
        <FileText size={11} /> {label} <ChevronDown size={10} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-52 py-1">
          {VARIABLES.map(v => (
            <button
              key={v.token}
              type="button"
              onClick={() => {
                const ta = textareaRef?.current;
                if (ta) {
                  const newVal = insertAtCursor(ta, v.token);
                  onChange(newVal);
                } else {
                  onChange((value || '') + v.token);
                }
                setOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-purple-50 text-gray-700 hover:text-purple-700"
            >
              <span className="font-mono text-purple-600 mr-1">{v.token}</span>
              <span className="text-gray-500">{v.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Seletor de template de email — carrega lista, aplica no form pai.
 */
export default function EmailTemplateSelector({ lead, onApply }) {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [previewTpl, setPreviewTpl] = useState(null);
  const dropRef = useRef(null);

  useEffect(() => {
    api.getEmailTemplates({ funnel_type: lead?.funnel_type })
      .then(setTemplates)
      .catch(() => {});
  }, [lead?.funnel_type]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function applyTemplate(tpl) {
    const subject = previewInterpolate(tpl.subject, lead);
    const body = previewInterpolate(tpl.body_text || tpl.body_html || '', lead);
    onApply({ subject, body });
    setOpen(false);
    setPreviewTpl(null);
  }

  if (!templates.length) return null;

  return (
    <>
      <div className="relative" ref={dropRef}>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-800 border border-purple-200 hover:border-purple-400 rounded-lg px-3 py-1.5 transition-colors"
        >
          <FileText size={13} />
          Usar template
          <ChevronDown size={11} />
        </button>

        {open && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 w-72 py-2">
            <p className="px-3 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Templates disponíveis</p>
            {templates.map(tpl => (
              <div
                key={tpl.id}
                className="flex items-center justify-between px-3 py-2 hover:bg-purple-50 cursor-pointer group"
              >
                <div className="flex-1 min-w-0" onClick={() => applyTemplate(tpl)}>
                  <p className="text-sm font-medium text-gray-800 truncate">{tpl.name}</p>
                  <p className="text-xs text-gray-400 truncate">{tpl.subject}</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setPreviewTpl(tpl); setOpen(false); }}
                  className="ml-2 text-gray-400 hover:text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Preview"
                >
                  <Eye size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview modal */}
      {previewTpl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <p className="font-semibold text-gray-800">{previewTpl.name}</p>
                <p className="text-xs text-gray-400">Preview com dados do lead</p>
              </div>
              <button type="button" onClick={() => setPreviewTpl(null)} className="text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-3">
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Assunto</p>
                <p className="text-sm text-gray-800 bg-gray-50 rounded-lg px-3 py-2">
                  {previewInterpolate(previewTpl.subject, lead)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Corpo</p>
                <pre className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 whitespace-pre-wrap font-sans">
                  {previewInterpolate(previewTpl.body_text || previewTpl.body_html || '', lead)}
                </pre>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setPreviewTpl(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
              <button type="button" onClick={() => applyTemplate(previewTpl)}
                className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">
                Usar este template
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
